import React, { ReactNode, useMemo, useState } from "react";
import styled, { css, useTheme } from "styled-components";
import moment from "moment";
import {
  BotGraphType,
  BotTemplateData,
  LanguageNameSpaces,
  TimeRange,
} from "@shared/types";
import { FormattedChartDataPoint } from "@shared/types/common";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useBotChart } from "@shared/features/bots/hooks";
import { BotGraphTypeToDisplay } from "@shared/features/bots/constants";
import {
  BASE_CHART_TIME_RANGES,
  ICON_TYPES,
  TIME_RANGE_1_DAY,
  TIME_RANGE_START,
} from "@shared/common/constants";
import { formatDateLabel, getDaysFromDate } from "@shared/utils/date";
import IconWithFallback from "Global/IconWithFallback";
import useMedia from "utils/hooks/useMedia";
import { compactAxisValue } from "features/bots/NewLook/newLookBotUtils";
import Panel from "common/NewLook/Panel.tsx";
import SegmentTabs from "common/NewLook/SegmentTabs.tsx";
import { PhoneHeroRanges } from "features/dashboard/PhoneDashboard/PhoneHeroControls.tsx";
import Skeleton from "common/NewLook/Skeleton.tsx";
import ChartEmpty from "common/NewLook/ChartEmpty.tsx";
import {
  directionColor,
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";
import NewLookProfitChart, {
  ProfitPoint,
} from "features/bots/NewLook/NewLookProfitChart";
import { newLookChip } from "features/bots/NewLook/newLookStyles";
import {
  getPublicBotChips,
  toPercentText,
  toSignedPercent,
} from "features/bots/PublicBots/publicBotDisplay";
import {
  BotSub,
  BotSubDot,
  BotSubPart,
} from "features/bots/PublicBots/newLookPublicBotStyles";

interface NewLookPublicBotPerformanceProps {
  botUUID: string;
  /** The bot's public figures (undefined while they load). */
  botData?: BotTemplateData;
  /** When the bot started (unix seconds). */
  created?: number;
  isDemoCopyPublic?: boolean;
  /** The bot's coin, name and market in the panel's header (the page). */
  showIdentity?: boolean;
  /** Narrow hosts (the copy drawer): the figures always sit under the chart. */
  stacked?: boolean;
}

type ChartKind = "accumulated" | "daily";
const CHART_KINDS: readonly ChartKind[] = ["accumulated", "daily"];
const TIME_RANGES = [
  TIME_RANGE_START,
  ...BASE_CHART_TIME_RANGES,
] as TimeRange[];
const CHART_HEIGHT = 220;

const toPoints = (data: FormattedChartDataPoint[] | undefined) =>
  (data || [])
    .filter((point) => point.rawX !== undefined)
    .map((point) => ({ time: Number(point.rawX), value: point.y }));

/**
 * New look: a public bot's results, built like the dashboard's overview —
 * the total profit as the headline over its chart (the headline follows the
 * crosshair on the accumulated profit), what the range added under it, and
 * beside it the bot's other key figures. Profit is P&L, so figures and
 * charts are coloured by direction.
 */
const NewLookPublicBotPerformance = ({
  botUUID,
  botData,
  created,
  isDemoCopyPublic,
  showIdentity,
  stacked,
}: NewLookPublicBotPerformanceProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const tokens = getNewLookTokens(useTheme());
  const isPhone = useMedia(["(max-width: 600px)"], [true], false);
  const [chartKind, setChartKind] = useState<ChartKind>("accumulated");
  const [timeRange, setTimeRange] = useState<TimeRange>(TIME_RANGE_START);
  const [hovered, setHovered] = useState<{
    kind: ChartKind;
    point: ProfitPoint;
  }>();

  const chartArgs = {
    publicBotUUID: botUUID,
    defaultTimeRange: TIME_RANGE_START as TimeRange,
    isDemoCopyPublic,
  };
  const accumulatedChart = useBotChart({
    ...chartArgs,
    chartType: BotGraphType.ACCUMULATING_PROFIT_PERCENTAGE,
  });
  const dailyChart = useBotChart({
    ...chartArgs,
    chartType: BotGraphType.PROFIT_BY_DAY_PERCENTAGE,
  });
  const accumulated = useMemo(
    () => toPoints(accumulatedChart.graphData),
    [accumulatedChart.graphData]
  );
  const daily = useMemo(
    () => toPoints(dailyChart.graphData),
    [dailyChart.graphData]
  );

  const changeRange = (range: TimeRange) => {
    setHovered(undefined);
    setTimeRange(range);
    accumulatedChart.setTimeRange(range);
    dailyChart.setTimeRange(range);
  };

  const isStart = timeRange === TIME_RANGE_START;
  const intraday = timeRange === TIME_RANGE_1_DAY;
  const rangeLabel = (range: TimeRange) =>
    range === TIME_RANGE_START
      ? t("public_bots.range_all")
      : range.toUpperCase();
  const formatDay = (time: number) =>
    formatDateLabel(moment.unix(time), intraday ? "MMM D, HH:mm" : "MMM D");

  // The line under the headline: what the range added (or, under the
  // crosshair, what was added since the range began / what the day made).
  // Since the start, the bot began at 0: the range added the bot's total
  // (the series' first point may already carry some profit).
  const rangeStart = accumulated[0];
  const rangeEnd = accumulated[accumulated.length - 1];
  const totalProfit = botData ? botData.change * 100 : 0;
  const baseline = isStart ? 0 : rangeStart?.value ?? 0;
  const headline =
    hovered?.kind === "accumulated" ? hovered.point.value : totalProfit;
  let changeValue = isStart
    ? totalProfit
    : rangeStart && rangeEnd
    ? rangeEnd.value - rangeStart.value
    : 0;
  let changeMeta =
    isStart && rangeStart
      ? t("performance.since", { time: formatDay(rangeStart.time) })
      : t("performance.over_range", { range: rangeLabel(timeRange) });
  if (hovered?.kind === "accumulated" && rangeStart) {
    changeValue = hovered.point.value - baseline;
    changeMeta = t("performance.since", { time: formatDay(rangeStart.time) });
  } else if (hovered?.kind === "daily") {
    changeValue = hovered.point.value;
    changeMeta = t("performance.on_day", {
      time: formatDay(hovered.point.time),
    });
  }

  const formatAxisValue = (value: number, step: number) =>
    compactAxisValue(value, step, { suffix: "%" });

  const renderChart = (kind: ChartKind) => {
    const points = kind === "accumulated" ? accumulated : daily;
    const fetching =
      kind === "accumulated"
        ? accumulatedChart.isFetching
        : dailyChart.isFetching;
    if (fetching && points.length < 2) {
      return <Skeleton width="100%" height={CHART_HEIGHT} radius={8} />;
    }
    if (points.length < 2) {
      return (
        <ChartEmpty height={CHART_HEIGHT}>
          {t("performance.no_history")}
        </ChartEmpty>
      );
    }
    const range = isStart ? t("public_bots.all_time") : rangeLabel(timeRange);
    return (
      <NewLookProfitChart
        points={points}
        height={CHART_HEIGHT}
        mode={kind === "accumulated" ? "line" : "bars"}
        intraday={intraday}
        formatAxisValue={formatAxisValue}
        ariaLabel={t(
          kind === "accumulated"
            ? "public_bots.chart_label"
            : "public_bots.daily_chart_label",
          { range }
        )}
        data-testid="public-bot-chart"
        onActiveChange={(point) =>
          setHovered((current) => {
            if (point) return { kind, point };
            return current?.kind === kind ? undefined : current;
          })
        }
      />
    );
  };

  // No figures yet (loading, or a link that leads nowhere): placeholders.
  const figuresLoading = !botData;
  const [headlineNumber] = toSignedPercent(headline).split("%");
  const headlineFigure = figuresLoading ? (
    <Skeleton width={180} height={40} style={{ marginTop: 4 }} />
  ) : (
    <Display style={{ color: directionColor(tokens, headline) }}>
      {headlineNumber}
      <Unit>%</Unit>
    </Display>
  );
  const hasHistory = accumulated.length > 1 || daily.length > 1;

  const daysRunning = created
    ? getDaysFromDate(created, true)
    : botData?.days_running;
  const metric = (
    key: string,
    label: ReactNode,
    value: ReactNode,
    color?: string,
    meta?: ReactNode
  ) => (
    <Metric key={key} $stacked={stacked}>
      <Label>{label}</Label>
      {figuresLoading ? (
        <Skeleton width={88} height={26} />
      ) : (
        <>
          <MetricValue style={{ color }}>{value}</MetricValue>
          {meta && <Meta>{meta}</Meta>}
        </>
      )}
    </Metric>
  );

  const symbol = botData?.symbols?.[0];
  const identity =
    showIdentity && !botData ? (
      <Skeleton width={220} height={14} />
    ) : (
      <Identity>
        <IconWithFallback
          iconType={ICON_TYPES.COIN}
          coinOrExchangeName={symbol}
          iconStyle={ICON_STYLE}
        />
        <IdentityName title={botData?.name}>{botData?.name}</IdentityName>
      </Identity>
    );
  const identityMeta = showIdentity && botData && (
    <BotSub>
      {[symbol, botData.exchange].filter(Boolean).map((part, index) => (
        <React.Fragment key={index}>
          {index > 0 && <BotSubDot />}
          <BotSubPart title={part}>{part}</BotSubPart>
        </React.Fragment>
      ))}
    </BotSub>
  );
  const chips = showIdentity && botData && (
    <Chips>
      {getPublicBotChips(botData).map((chip) => (
        <Chip key={chip}>{chip}</Chip>
      ))}
    </Chips>
  );

  return (
    <Panel
      flush={true}
      title={showIdentity ? identity : undefined}
      extra={(!isPhone && identityMeta) || undefined}
      actions={(!isPhone && chips) || undefined}
      data-testid="public-bot-performance"
    >
      <Container>
        <Layout $stacked={stacked}>
          <Main>
            {isPhone && (identityMeta || chips) && (
              <PhoneIdentity>
                {identityMeta}
                {chips}
              </PhoneIdentity>
            )}
            <MainHeader>
              <div>
                <Label>{t("public_bots.total_profit")}</Label>
                {headlineFigure}
                {hasHistory && (
                  <ChangeLine>
                    <ChangeValue
                      style={{ color: directionColor(tokens, changeValue) }}
                    >
                      {toSignedPercent(changeValue)}
                    </ChangeValue>
                    <Meta>{changeMeta}</Meta>
                  </ChangeLine>
                )}
              </div>
              <Controls>
                <SegmentTabs<ChartKind>
                  options={CHART_KINDS}
                  value={chartKind}
                  onChange={(kind) => {
                    setHovered(undefined);
                    setChartKind(kind);
                  }}
                  ariaLabel={t("performance.chart_type_label")}
                  getLabel={(kind) =>
                    t(
                      BotGraphTypeToDisplay[
                        kind === "accumulated"
                          ? BotGraphType.ACCUMULATING_PROFIT
                          : BotGraphType.PROFIT_BY_DAY
                      ]
                    )
                  }
                />
                {!isPhone && (
                  <SegmentTabs<TimeRange>
                    options={TIME_RANGES}
                    value={timeRange}
                    onChange={changeRange}
                    ariaLabel={t("performance.range_label")}
                    getLabel={rangeLabel}
                  />
                )}
              </Controls>
            </MainHeader>
            <ChartArea>{renderChart(chartKind)}</ChartArea>
            {isPhone && (
              <PhoneHeroRanges<TimeRange>
                options={TIME_RANGES}
                value={timeRange}
                onChange={changeRange}
                label={t("performance.range_label")}
                surface="panel"
                getLabel={rangeLabel}
              />
            )}
          </Main>
          <Side $stacked={stacked}>
            {metric(
              "last_90_days",
              t("public_bots.last_90_days"),
              botData && toPercentText(botData.last_90_days_change),
              directionColor(tokens, botData?.last_90_days_change),
              botData &&
                t("public_bots.last_month_value", {
                  value: toPercentText(botData.last_month_change),
                })
            )}
            {metric(
              "avg_monthly",
              t("public_bots.avg_monthly"),
              botData && toPercentText(botData.average_monthly_profit),
              directionColor(tokens, botData?.average_monthly_profit)
            )}
            {metric(
              "days_running",
              t("public_bots.days_running"),
              daysRunning,
              undefined,
              created &&
                t("public_bots.running_since", {
                  date: formatDateLabel(moment.unix(created), "MMM D, YYYY"),
                })
            )}
          </Side>
        </Layout>
      </Container>
    </Panel>
  );
};

export default React.memo(NewLookPublicBotPerformance);

const ICON_STYLE = { width: 24, height: 24, marginRight: 0 };
/** Below this width the figures move under the chart, in one row. */
const STACK_BELOW = 860;

const stackedSide = css`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  column-gap: 24px;
  padding: 8px 24px 20px;
  border-left: none;
  border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  border-radius: 0 0 ${NEW_LOOK_RADIUS.panel}px ${NEW_LOOK_RADIUS.panel}px;
  @media (max-width: 600px) {
    column-gap: 16px;
    padding: 4px 16px 16px;
  }
`;
const stackedMetric = css`
  padding: 8px 0 0;
  & + & {
    border-top: none;
  }
`;

const Container = styled.div`
  container-type: inline-size;
  min-width: 0;
`;
const Layout = styled.div<{ $stacked?: boolean }>`
  display: grid;
  grid-template-columns: ${(p) =>
    p.$stacked ? "minmax(0, 1fr)" : "minmax(0, 1fr) 280px"};
  @container (max-width: ${STACK_BELOW}px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
const Main = styled.div`
  min-width: 0;
  padding: 20px 20px 16px 24px;
  @media (max-width: 600px) {
    padding: 16px;
  }
`;
const MainHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px 16px;
`;
// In a narrow panel the controls take their own row under the headline:
// the chart type on the left, the range on the right.
const Controls = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  margin-left: auto;
  @container (max-width: 720px) {
    width: 100%;
    justify-content: space-between;
    margin-left: 0;
  }
`;
const Label = styled.div`
  ${newLookType.label};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const Display = styled.div`
  margin-top: 4px;
  ${newLookType.display};
  white-space: nowrap;
  @media (max-width: 600px) {
    font-size: 30px;
    line-height: 36px;
  }
`;
const Unit = styled.span`
  margin-left: 1px;
  font-size: 0.72em;
  font-weight: 500;
  @media (max-width: 600px) {
    font-size: 22px;
  }
`;
const ChangeLine = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 6px;
`;
const ChangeValue = styled.span`
  ${newLookType.meta};
  font-size: 13px;
  font-weight: 500;
`;
const Meta = styled.span`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const ChartArea = styled.div`
  margin-top: 20px;
`;
const Side = styled.div<{ $stacked?: boolean }>`
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 6px 24px 20px;
  border-left: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  background: ${(p) => getNewLookTokens(p.theme).well};
  border-radius: 0 0 ${NEW_LOOK_RADIUS.panel}px 0;
  ${(p) => p.$stacked && stackedSide};
  @container (max-width: ${STACK_BELOW}px) {
    ${stackedSide};
  }
`;
const Metric = styled.div<{ $stacked?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  padding: 14px 0;
  & + & {
    border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
  &:last-child {
    padding-bottom: 0;
  }
  ${(p) => p.$stacked && stackedMetric};
  @container (max-width: ${STACK_BELOW}px) {
    ${stackedMetric};
  }
`;
const MetricValue = styled.div`
  ${newLookType.figureLarge};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
  @media (max-width: 600px) {
    font-size: 17px;
    line-height: 22px;
  }
`;
const Identity = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;
const IdentityName = styled.span`
  max-width: min(360px, 40vw);
  @media (max-width: 600px) {
    max-width: calc(100vw - 120px);
  }
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;
// Phone: the market and settings under the panel's title, not beside it.
const PhoneIdentity = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
`;
const Chips = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;
const Chip = styled.span`
  ${newLookChip};
`;
